import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  UserCircle,
  Mail,
  Phone,
  Calendar,
  Lock,
  Shield,
  Edit3,
  Save,
  X,
  Camera,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  Key,
  Hash,
  FileText,
  Building,
} from 'lucide-react';
import './MyProfile.css';

const MyProfile = () => {
  const { isAdmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePhoto, setProfilePhoto] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Form states for profile info (extended for admins)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    dateOfBirth: '',
    // Admin-only editable fields
    email: '',
    cin: '',
    cnss: '',
    contractType: 'CDI',
  });

  // Form states for password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }
  const fileRef = useRef(null);

  const getPhotoStorageKey = (p) => `profile_photo_${p?.id || p?.userId || 'me'}`;

  const loadProfile = async () => {
    try {
      setLoading(true);
      // Try /users/me first, fallback to /auth/profile
      let p;
      try {
        p = await api.get('/users/me');
      } catch (e) {
        p = await api.get('/auth/profile');
      }
      setProfile(p);
      setFormData({
        firstName: p?.firstName || '',
        lastName: p?.lastName || '',
        username: p?.username || '',
        phoneNumber: p?.phoneNumber || '',
        dateOfBirth: p?.dateOfBirth ? p.dateOfBirth.split('T')[0] : '',
        // Admin-only fields (pre-fill)
        email: p?.email || '',
        cin: p?.cin || '',
        cnss: p?.cnss || '',
        contractType: p?.contractType || 'CDI',
      });

      const savedPhoto = localStorage.getItem(getPhotoStorageKey(p));
      if (savedPhoto) setProfilePhoto(savedPhoto);
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Impossible de charger le profil' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const onSelectPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setProfilePhoto(dataUrl);
      localStorage.setItem(getPhotoStorageKey(profile), dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);
    try {
      let updated;
      if (isAdmin() && profile?.id) {
        // Admins use the full update endpoint allowing all fields
        const payload = {
          firstName: formData.firstName?.trim() || undefined,
          lastName: formData.lastName?.trim() || undefined,
          username: formData.username?.trim() || undefined,
          phoneNumber: formData.phoneNumber?.trim() || undefined,
          dateOfBirth: formData.dateOfBirth && formData.dateOfBirth.trim() !== '' ? formData.dateOfBirth : null,
          email: formData.email?.trim() || undefined,
          cin: formData.cin?.trim() || undefined,
          cnss: formData.cnss?.trim() || undefined,
          contractType: formData.contractType || undefined,
        };
        updated = await api.patch(`/users/${profile.id}`, payload);
      } else {
        // Regular users use self-update (limited fields)
        const payload = {
          firstName: formData.firstName?.trim() || undefined,
          lastName: formData.lastName?.trim() || undefined,
          username: formData.username?.trim() || undefined,
          phoneNumber: formData.phoneNumber?.trim() || undefined,
          dateOfBirth: formData.dateOfBirth && formData.dateOfBirth.trim() !== '' ? formData.dateOfBirth : null,
        };
        updated = await api.patch('/users/me', payload);
      }
      setProfile(updated);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({
        type: 'error',
        text: err?.message || 'Failed to update profile',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Le nouveau mot de passe doit comporter au moins 6 caractères',
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Les nouveaux mots de passe ne correspondent pas',
      });
      return;
    }

    setSaving(true);
    try {
      await api.patch('/users/me', {
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (err) {
      console.error('Error changing password:', err);
      setMessage({
        type: 'error',
        text: err?.message || 'Failed to change password',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loading">Chargement du profil...</div>;

  return (
    <div className="my-profile-page">
      <div className="page-header">
        <div>
          <h1>
            <UserCircle size={26} /> Mon Profil
          </h1>
          <p className="page-subtitle">Gérez vos informations personnelles et paramètres de compte</p>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
              <Edit3 size={16} /> Modifier mes infos
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={() => setIsEditing(false)}>
              <X size={16} /> Annuler
            </button>
          )}
          <button
            className={`btn ${isChangingPassword ? 'btn-secondary' : 'btn-outline-primary'}`}
            onClick={() => {
              setIsChangingPassword(!isChangingPassword);
              setMessage(null);
            }}
          >
            <Key size={16} /> {isChangingPassword ? 'Fermer' : 'Changer mot de passe'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`profile-alert alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Password Change Form Drawer/Modal */}
      {isChangingPassword && (
        <div className="profile-section-card password-card">
          <div className="card-header">
            <h3><Key size={20} /> Modifier le mot de passe</h3>
          </div>
          <form onSubmit={handleSavePassword} className="password-form">
            <div className="form-grid">
              <div className="form-group">
                <label>Mot de passe actuel</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Entrez votre mot de passe actuel"
                  required
                />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Au moins 6 caractères"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Répétez le nouveau mot de passe"
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <Save size={16} /> {saving ? 'Enregistrement...' : 'Mettre à jour le mot de passe'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="profile-layout">
        {/* Left Column: Avatar & Summary Card */}
        <div className="profile-sidebar">
          <div className="profile-card avatar-card">
            <div className="photo-wrap" onClick={() => fileRef.current?.click()} title="Changer la photo">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="profile-photo" />
              ) : (
                <div className="profile-photo-placeholder">
                  {(profile?.firstName?.[0] || profile?.email?.[0] || '?').toUpperCase()}
                </div>
              )}
              <div className="photo-overlay">
                <Camera size={16} />
                <span>Changer photo</span>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onSelectPhoto}
            />
            <h2 className="user-fullname">
              {`${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || 'Utilisateur'}
            </h2>
            <div className="badge-role">
              <Shield size={14} />
              <span>{profile?.role ? profile.role.toUpperCase() : 'EMPLOYEE'}</span>
            </div>
            {profile?.matricule && (
              <div className="matricule-pill">
                <Hash size={13} /> {profile.matricule}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Information Sections */}
        <div className="profile-main-content">
          {/* Editable Personal Information */}
          <div className="profile-section-card">
            <div className="card-header">
              <h3>
                <UserCircle size={20} /> Personal Information
              </h3>
              <span className="info-subtitle">
                {isEditing
                  ? isAdmin()
                    ? 'Edit all your profile fields (Admin)'
                    : 'Edit your allowed fields'
                  : 'Editable fields'}
              </span>
            </div>

            {isEditing ? (
              <form onSubmit={handleSaveProfile} className="profile-edit-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="First name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Username"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="+216 ..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                    />
                  </div>
                  {isAdmin() ? (
                    // Admin-only editable fields
                    <>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="email@company.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>CIN</label>
                        <input
                          type="text"
                          name="cin"
                          value={formData.cin}
                          onChange={handleInputChange}
                          placeholder="8 digits"
                          maxLength={8}
                        />
                      </div>
                      <div className="form-group">
                        <label>N° CNSS</label>
                        <input
                          type="text"
                          name="cnss"
                          value={formData.cnss}
                          onChange={handleInputChange}
                          placeholder="CNSS number"
                        />
                      </div>
                      <div className="form-group">
                        <label>Contract Type</label>
                        <select
                          name="contractType"
                          value={formData.contractType}
                          onChange={handleInputChange}
                        >
                          <option value="CDI">CDI</option>
                          <option value="CIVP">CIVP</option>
                          <option value="INTERNSHIP_SUMMER">Internship (Summer)</option>
                          <option value="INTERNSHIP_END_OF_STUDY">Internship (End of Study)</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <div className="form-group read-only-field">
                      <label>
                        Email <Lock size={12} title="Cannot be changed" />
                      </label>
                      <input type="email" value={profile?.email || ''} disabled readOnly />
                      <small className="field-note">Email cannot be changed by the user.</small>
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setIsEditing(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-details-grid">
                <div className="info-item">
                  <span className="info-label"><UserCircle size={15} /> First Name</span>
                  <span className="info-value">{profile?.firstName || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label"><UserCircle size={15} /> Last Name</span>
                  <span className="info-value">{profile?.lastName || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label"><UserCircle size={15} /> Username</span>
                  <span className="info-value">{profile?.username || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <Mail size={15} /> Email
                    {!isAdmin() && <Lock size={12} className="lock-icon" title="Cannot be changed" />}
                  </span>
                  <span className="info-value">{profile?.email || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label"><Phone size={15} /> Phone</span>
                  <span className="info-value">{profile?.phoneNumber || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label"><Calendar size={15} /> Date of Birth</span>
                  <span className="info-value">
                    {profile?.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString('en-GB') : '-'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Read-Only Employment & Administrative Information */}
          <div className="profile-section-card read-only-section">
            <div className="card-header">
              <h3>
                <Briefcase size={20} /> Professional Information
              </h3>
              {!isAdmin() && (
                <span className="lock-badge">
                  <Lock size={12} /> Set by Administration
                </span>
              )}
            </div>

            <div className="info-details-grid">
              <div className="info-item">
                <span className="info-label"><Hash size={15} /> Matricule</span>
                <span className="info-value highlight">{profile?.matricule || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><FileText size={15} /> CIN</span>
                <span className="info-value">{profile?.cin || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><FileText size={15} /> Contract Type</span>
                <span className="info-value">{profile?.contractType || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><FileText size={15} /> N° CNSS</span>
                <span className="info-value">{profile?.cnss || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label"><Calendar size={15} /> Hire Date</span>
                <span className="info-value">
                  {profile?.hireDate ? new Date(profile.hireDate).toLocaleDateString('en-GB') : '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label"><Building size={15} /> Département</span>
                <span className="info-value">
                  {profile?.department?.name || profile?.departmentName || profile?.department || '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label"><Shield size={15} /> Rôle</span>
                <span className="info-value badge-text">{profile?.role ? profile.role.toUpperCase() : '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
